<template>
  <div class="view-item-container">
    <div
      class="view-item-row"
      @click="handleApplyView(record)"
    >
      <a-tooltip :title="record.name">
        <div class="view-name-container">
          <span class="view-name">{{ record.name }}</span>
          <a-tag
            v-if="record.is_default"
            color="blue"
            size="small"
            class="view-default-tag"
          >
            {{ $t('默认') }}
          </a-tag>
        </div>
      </a-tooltip>

      <span
        class="view-num"
        style="float: right"
        :style="currentView?.id === record.id ? { color: '#1890ff', fontWeight: 'bold' } : {}"
        v-if="record.num"
      >
        <a-tooltip :title="record.num">
          {{ record.num }}
        </a-tooltip>
      </span>
    </div>
    <div
      :class="{ 'view-actions': true, current: currentView?.id === record.id }"
    >
      <a-tooltip :title="$t('设为默认')">
        <a-button
          type="text"
          size="small"
          @click="handleSetDefault(record)"
        >
          <template #icon>
            <star-filled
              v-if="record.is_default"
              style="color: #fadb14"
            />
            <star-outlined v-else />
          </template>
        </a-button>
      </a-tooltip>
      <a-button
        type="text"
        size="small"
        @click="handleEditView(record)"
      >
        <template #icon><edit-outlined /></template>
      </a-button>
      <a-button
        type="text"
        size="small"
        danger
        @click="handleDeleteView(record)"
      >
        <template #icon><delete-outlined /></template>
      </a-button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useUserStore } from 'liyu-pc-base/init-plugin';
import { message, Modal } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import { reactive, createVNode } from 'vue';
import modalBox from 'liyu-pc-base/components/form-modal/modal-tools';
import {
  DeleteOutlined,
  EditOutlined,
  StarOutlined,
  StarFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons-vue';
interface Props {
  fetchViews: Function;
  record: any;
  currentView: any;
  setCurrentView: Function;
  setViewData: Function;
}
const props = defineProps<Props>();
import {
  delete_view_settings,
  update_view_settings,
  set_view_settings_default,
  refresh_view_settings_cache,
} from 'liyu-pc-base/api/view-model';

const userStore = useUserStore();
const current_org = userStore.currentOrg;
// ★ v24（决策 24）：不做按钮权限校验 —— 不 CHECK_AUTH(VIEW_SYSTEM_EDIT)，
// 系统视图的修改/删除/设默认操作按钮恒展示。
const handleApplyView = (view: any) => {
  if (props.currentView?.id === view.id) {
    props.setCurrentView({ id: 'no-view' });
    props.setViewData(null);
  } else {
    props.setCurrentView(view);
    props.setViewData(view.data);
  }
};
const handleSetDefault = async (view: any) => {
  try {
    await set_view_settings_default({
      org_id: current_org.id,
      view_settings_id: view.id,
    });
    await refresh_view_settings_cache({
      org_id: current_org.id,
      view_settings_id: view.id,
    });
    message.success(t('设置默认成功'));
    props.fetchViews();
  } catch (e) {
    message.error(t('设置默认失败'));
  }
};

const handleEditView = (record: any) => {
  const tempModal = reactive({
    open: true,
    loading: false,
    title: t('编辑视图'),
    form: {
      settings: { labelAlign: 'right', layout: 'vertical', col: [] },
      fields: [
        {
          type: 'input',
          name: 'name',
          label: t('视图名称'),
          disabled: false,
          allowClear: true,
          inputType: 'text',
          defaultValue: record.name,
          placeholder: t('请输入视图名称'),
        },
      ],
      rules: reactive({
        name: [{ required: true, message: t('请输入视图名称') }],
      }),
      model: reactive({
        name: record.name,
        is_system: [record.is_system || false],
      }),
    },
    options: {
      is_system: [{ name: t('设为系统视图'), value: true }],
    },
    ok: async (data: any) => {
      await update_view_settings({
        org_id: current_org.id,
        view_settings_id: record.id,
        name: data.name,
        is_system: data.is_system && data.is_system[0],
      });
      message.success(t('修改成功'));
      props.fetchViews();
    },
  });

  // ★ v24：系统视图选项不再做权限门控，恒展示
  tempModal.form.fields.push({
    type: 'checkbox',
    name: 'is_system',
    label: '',
    disabled: false,
    allowClear: true,
    labelKey: 'name',
    valueKey: 'value',
    defaultValue: [false],
  } as any);
  modalBox(tempModal);
};
const handleDeleteView = (view: any) => {
  Modal.confirm({
    title: t('确认删除视图？'),
    content: t('视图删除后不可恢复，是否确认删除？'),
    icon: createVNode(ExclamationCircleOutlined),
    onOk: async () => {
      await delete_view_settings({
        org_id: current_org.id,
        view_settings_id: view.id,
      });
      message.success(t('删除成功'));
      props.fetchViews();
    },
  });
};
</script>
<style scoped lang="less">
.view-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;

  .view-name-container {
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 4px 0;

    .view-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 0 1 auto;
      &:hover {
        color: #1890ff;
      }
    }

    .view-default-tag {
      flex: 0 0 auto;
      margin-left: 4px;
    }
  }
}
.view-item-container {
  position: relative;
  .view-actions {
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
    position: absolute;
    align-items: center;
    top: 0;
    right: 0;
    border-radius: 4px;
    height: 30px;
    background: #f9f9f9;
    &.current {
      background: #b9defd;
    }
  }

  &:hover .view-actions {
    opacity: 1;
  }
}
</style>
